<template>
  <div
    v-if="item.modifier_groups?.length"
    class="space-y-3"
  >
    <div
      v-for="groupLink in item.modifier_groups"
      :key="groupLink.group_id"
      class="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <!-- Accordion Header -->
      <button
        type="button"
        class="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        @click="$emit('toggle-accordion', groupLink.group_id)"
      >
        <div class="flex items-center gap-2">
          <p class="text-sm font-medium text-gray-900 dark:text-gray-100">
            {{ groupLink.group.name }}
          </p>
          <div class="flex items-center gap-1">
            <span
              v-if="groupLink.required"
              class="inline-flex items-center rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-200"
            >
              Requerido
            </span>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Min {{ groupLink.min_select }} · Max {{ groupLink.max_select }}
            </p>
          </div>
        </div>
        <svg
          class="w-4 h-4 text-gray-500 transition-transform duration-200"
          :class="{ 'rotate-180': isAccordionOpen(groupLink.group_id) }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      
      <!-- Accordion Content -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-96 opacity-100"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="max-h-96 opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <div
          v-show="isAccordionOpen(groupLink.group_id)"
          class="border-t border-gray-200 dark:border-gray-700"
        >
          <div class="p-3 space-y-2">
            <button
              v-for="option in groupLink.group.options"
              :key="option.id"
              type="button"
              class="w-full flex items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors"
              :class="isOptionSelected(groupLink.group_id, option.id)
                ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800'"
              @click="$emit('toggle-option', groupLink.group_id, option.id, groupLink.max_select)"
            >
              <span>{{ option.name }}</span>
              <span class="text-xs">{{ option.price_delta > 0 ? `+$${option.price_delta.toFixed(2)}` : '$0.00' }}</span>
            </button>
          </div>
          <div
            v-if="!isGroupSelectionValid(groupLink)"
            class="px-3 pb-3"
          >
            <p class="text-xs text-red-600 dark:text-red-400">
              <span v-if="groupLink.required">
                <span v-if="groupLink.min_select === 1 && groupLink.max_select === 1">
                  Este grupo es requerido. Selecciona 1 opción.
                </span>
                <span v-else>
                  Este grupo es requerido. Debes seleccionar entre {{ groupLink.min_select }} y {{ groupLink.max_select }} opciones.
                </span>
              </span>
              <span v-else>
                <span v-if="groupLink.min_select === 1 && groupLink.max_select === 1">
                  Selecciona 1 opción.
                </span>
                <span v-else>
                  Selecciona entre {{ groupLink.min_select }} y {{ groupLink.max_select }} opciones.
                </span>
              </span>
            </p>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ExtendedMenuItem, MenuItemModifierGroupLink } from '@/types/order';

interface Props {
  item: ExtendedMenuItem;
  isOptionSelected: (groupId: number, optionId: number) => boolean;
  isAccordionOpen: (groupId: number) => boolean;
  isGroupSelectionValid: (groupLink: MenuItemModifierGroupLink) => boolean;
}

defineProps<Props>();

defineEmits<{
  'toggle-accordion': [groupId: number];
  'toggle-option': [groupId: number, optionId: number, maxSelect: number];
}>();
</script>
